import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import DateInput from "@/components/DateInput";

export function StatCard({ icon: Icon, label, value, accent = "#4F46E5", sub, testid, delay = 0 }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45, delay }}
      className="ep-card p-5"
      data-testid={testid}
    >
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">{label}</p>
          <p className="mt-2 text-3xl font-bold font-head text-white">{value}</p>
          {sub && <p className="mt-1 text-xs text-slate-500">{sub}</p>}
        </div>
        <div
          className="flex h-11 w-11 items-center justify-center rounded-xl"
          style={{ background: `${accent}22`, color: accent }}
        >
          <Icon size={22} />
        </div>
      </div>
    </motion.div>
  );
}

export function Modal({ open, onClose, title, children, testid, wide }) {
  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[60] flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
          <motion.div
            initial={{ scale: 0.94, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.94, opacity: 0, y: 20 }}
            transition={{ type: "spring", damping: 22, stiffness: 300 }}
            className={`ep-glass relative z-10 w-full ${wide ? "max-w-4xl" : "max-w-lg"} rounded-2xl p-6 shadow-2xl max-h-[90vh] overflow-y-auto`}
            data-testid={testid}
          >
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold font-head text-white">{title}</h3>
              <button onClick={onClose} className="text-slate-400 hover:text-white" data-testid="modal-close-btn">
                <X size={20} />
              </button>
            </div>
            {children}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export function Field({ label, type, ...props }) {
  if (type === "date") {
    return (
      <div className="block">
        <span className="mb-1.5 block text-xs font-medium text-slate-400">{label}</span>
        <DateInput {...props} />
      </div>
    );
  }
  return (
    <label className="block">
      <span className="mb-1.5 block text-xs font-medium text-slate-400">{label}</span>
      <input type={type} className="ep-input" {...props} />
    </label>
  );
}

export function Badge({ children, color = "#4F46E5" }) {
  return (
    <span
      className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold"
      style={{ background: `${color}22`, color }}
    >
      {children}
    </span>
  );
}

export function PageHeader({ title, subtitle, action }) {
  return (
    <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
      <div>
        <h1 className="text-2xl font-bold font-head text-white sm:text-3xl">{title}</h1>
        {subtitle && <p className="mt-1 text-sm text-slate-400">{subtitle}</p>}
      </div>
      {action}
    </div>
  );
}

export function Select({ label, children, ...props }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-xs font-medium text-slate-400">{label}</span>
      <select className="ep-input" {...props}>{children}</select>
    </label>
  );
}

export function Tabs({ tabs, active, onChange, testid = "tabs" }) {
  return (
    <div className="mb-6 inline-flex flex-wrap gap-1 rounded-2xl border border-slate-800/80 bg-slate-900/60 p-1" data-testid={testid}>
      {tabs.map((t) => (
        <button
          key={t.key}
          onClick={() => onChange(t.key)}
          data-testid={`${testid}-${t.key}`}
          className={`flex items-center gap-1.5 rounded-xl px-4 py-2 text-sm font-medium transition-all duration-200 ${
            active === t.key ? "bg-indigo-600 text-white shadow-lg shadow-indigo-500/30" : "text-slate-400 hover:bg-slate-800/60 hover:text-white"
          }`}
        >
          {t.icon && <t.icon size={15} />} {t.label}
        </button>
      ))}
    </div>
  );
}

export function Empty({ children }) {
  return <p className="py-10 text-center text-sm text-slate-500">{children}</p>;
}
